import React from 'react';
import PropTypes from 'prop-types';

export const additionalBenefitsIntroDescription = (
  <>
    <p>
      These are some examples of additional benefits you may be eligible for:
    </p>
    <ul>
      <li>Money to modify your home or vehicle to make it more accessible</li>
      <li>
        Home-based care if you need help with everyday activities or are
        housebound because of a service-connected condition or disability
      </li>
      <li>
        Individual Unemployability benefits if a service-connected condition or
        disability prevents you from keeping a job. This includes increased
        disability payments.
      </li>
    </ul>
  </>
);

export const additionalBenefitsIntroNote = (
  <p>
    <strong>Note:</strong> Once you answer these questions, we’ll let you know
    if you may be eligible. We’ll also let you know which forms you’ll need to
    fill out to apply.
  </p>
);

export const adaptiveHousingDescription =
  'You may be eligible for adapted housing grants to modify your home and make it more accessible. You can also use this grant to buy a home that meets your needs.';

export const adaptiveAutoDescription =
  'You may be eligible for automobile allowance benefits if your service-connected disability prevents you from driving. The automobile allowance can help you buy or adapt a vehicle so you can drive.';

export const aidAndAttendanceDescription = (
  <>
    <p>
      You may be eligible for Aid and Attendance benefits or Housebound
      allowance if you need help with daily activities or are housebound.
    </p>
    <p>
      You may be eligible for this benefit if you get a VA pension and you spend
      most of your time in your home because of a permanent disability.
    </p>
  </>
);

export const individualUnemployabilityHint =
  'This doesn’t include being retired, unless your doctor advised you to for medical reasons.';

export const individualUnemployabilityDescription =
  'If you can’t work because of a service-connected condition or disability, you may be eligible for Individual Unemployability benefits. This includes increased disability payments.';

const FormSection = ({ heading, formNumbers, children }) => (
  <div className="vads-u-padding-y--1">
    <h4 className="vads-u-margin-top--0 vads-u-margin-bottom--0">{heading}</h4>
    <p className="vads-u-margin-y--0 vads-u-font-size--sm">{formNumbers}</p>
    {children}
  </div>
);

FormSection.propTypes = {
  children: PropTypes.node,
  formNumbers: PropTypes.string,
  heading: PropTypes.string,
};

const FormLink = ({ href, text }) => (
  <p className="vads-u-margin-y--0">
    <va-link href={href} text={text} external />
  </p>
);

FormLink.propTypes = {
  href: PropTypes.string,
  text: PropTypes.string,
};

export const AdditionalBenefitsSummaryDescription = ({ formData }) => (
  <>
    <p>
      Based on your answers, you may be eligible for these additional disability
      benefits.
    </p>
    <div className="vads-u-background-color--gray-lightest vads-u-padding-y--1 vads-u-padding-x--3">
      <h3 className="vads-u-margin-top--0 vads-u-margin-bottom--1 vads-u-padding-top--1 vads-u-padding-bottom--0p5 vads-u-border-bottom--1px vads-u-border-color--gray-light">
        Additional forms
      </h3>
      {formData?.['view:modifyingHome'] && (
        <FormSection heading="Adapted housing" formNumbers="VA Form 26-4555">
          <p className="vads-u-margin-y--0">
            You can apply for an adapted housing grant to make your current home
            more accessible or to buy a home that meets your needs.
          </p>
          <FormLink
            href="https://www.va.gov/forms/26-4555/"
            text="Get VA Form 26-4555"
          />
        </FormSection>
      )}
      {formData?.['view:modifyingCar'] && (
        <FormSection
          heading="Automobile allowance"
          formNumbers="VA Form 21-4502"
        >
          <p className="vads-u-margin-y--0">
            You can apply for automobile allowance benefits if your
            service-connected disability prevents you from driving.
          </p>
          <FormLink
            href="https://www.va.gov/forms/21-4502/"
            text="Get VA Form 21-4502"
          />
          {formData?.['view:alreadyClaimedVehicleAllowance'] && (
            <>
              <p className="vads-u-margin-top--2 vads-u-margin-bottom--0">
                If you’ve already used this benefit before, you may be able to
                apply for a second automobile allowance.
              </p>
              <FormLink
                href="https://www.va.gov/disability/eligibility/special-claims/automobile-allowance-adaptive-equipment/"
                text="Learn more about the eligibility requirements for a second automobile allowance"
              />
            </>
          )}
        </FormSection>
      )}
      {formData?.['view:modifyingCar'] && (
        <FormSection
          heading="Conveyance and adaptive equipment"
          formNumbers="VA Form 10-1394"
        >
          <p className="vads-u-margin-y--0">
            You can apply for adaptive equipment for a vehicle you already own.
          </p>
          <FormLink
            href="https://www.va.gov/forms/10-1394/"
            text="Get VA Form 10-1394"
          />
        </FormSection>
      )}
      {formData?.['view:aidAndAttendance'] && (
        <FormSection heading="Aid and attendance" formNumbers="VA Form 21-2680">
          <p className="vads-u-margin-y--0">
            You can apply for aid and attendance benefits if you get a VA
            pension and you spend most of your time in your home because of a
            permanent disability.
          </p>
          <FormLink
            href="https://www.va.gov/forms/21-2680/"
            text="Get VA Form 21-2680"
          />
        </FormSection>
      )}
      {formData?.['view:unemployable'] && (
        <FormSection
          heading="Individual unemployability"
          formNumbers="VA Form 21-8940 and 21-4192"
        >
          <p className="vads-u-margin-y--0">
            You can file a claim for individual unemployability. You’ll need to
            fill out these 2 forms:
          </p>
          <p className="vads-u-margin-top--1 vads-u-margin-bottom--0">
            A Veteran’s Application for Increased Compensation Based on
            Unemployability (VA Form 21-8940)
          </p>
          <FormLink
            href="https://www.va.gov/forms/21-8940/"
            text="Get VA Form 21-8940"
          />
          <p className="vads-u-margin-top--1 vads-u-margin-bottom--0">
            A Request for Employment Information in Connection with Claim for
            Disability Benefits (VA Form 21-4192)
          </p>
          <FormLink
            href="https://www.va.gov/forms/21-4192/"
            text="Get VA Form 21-4192"
          />
        </FormSection>
      )}
    </div>
  </>
);

AdditionalBenefitsSummaryDescription.propTypes = {
  formData: PropTypes.shape({
    'view:alreadyClaimedVehicleAllowance': PropTypes.bool,
    'view:aidAndAttendance': PropTypes.bool,
    'view:modifyingCar': PropTypes.bool,
    'view:modifyingHome': PropTypes.bool,
    'view:unemployable': PropTypes.bool,
  }),
};
